/**
 * App.jsx
 * Layout and routing only.
 */

import { Navigate, Route, Routes } from 'react-router-dom';
import Sidebar from './components/Sidebar.jsx';
import Topbar from './components/Topbar.jsx';
import Placeholder from './components/Placeholder.jsx';
import Dashboard from './pages/Dashboard.jsx';
import Brands from './pages/Brands.jsx';
import Create from './pages/Create.jsx';
import Calendar from './pages/Calendar.jsx';
import Drafts from './pages/Drafts.jsx';
import Inbox from './pages/Inbox.jsx';
import Campaigns from './pages/Campaigns.jsx';
import Copilot from './pages/Copilot.jsx';
import Predictor from './pages/Predictor.jsx';
import Listening from './pages/Listening.jsx';
import Repurpose from './pages/Repurpose.jsx';
import Campaign from './pages/Campaign.jsx';
import Library from './pages/Library.jsx';
import Import from './pages/Import.jsx';
import Resize from './pages/Resize.jsx';
import Evergreen from './pages/Evergreen.jsx';
import AutoSchedule from './pages/AutoSchedule.jsx';
import Hashtags from './pages/Hashtags.jsx';
import BioPage from './pages/BioPage.jsx';
import Team from './pages/Team.jsx';
import Competitors from './pages/Competitors.jsx';
import Costs from './pages/Costs.jsx';
import Export from './pages/Export.jsx';
import Settings from './pages/Settings.jsx';
import Analytics from './pages/Analytics.jsx';
import Website from './pages/Website.jsx';
import Ads from './pages/Ads.jsx';
import AdBuilder from './pages/AdBuilder.jsx';
import Video from './pages/Video.jsx';
import MobileTabBar from './components/MobileTabBar.jsx';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { setSignedOutHandler } from './services/api.js';
import { useSession } from './hooks/useSession.jsx';
import { useWaiting } from './hooks/useWaiting.jsx';

export default function App() {
  const { user } = useSession();
  const waiting = useWaiting();
  const here = useLocation().pathname;
  // Set by any request that discovers Access has signed us out. Shown once,
  // over everything, because no screen can do anything useful until it is
  // resolved and each one guessing separately is how "signed out" came to
  // look like "you have no brands".
  const [signedOut, setSignedOut] = useState(false);
  // The sidebar as a drawer, on a phone. Above 900px it is always shown and
  // this does nothing.
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setSignedOutHandler(() => setSignedOut(true));
    return () => setSignedOutHandler(null);
  }, []);

  // Choosing a screen is also what dismisses the drawer.
  useEffect(() => setMenuOpen(false), [here]);

  // While the drawer is open: Escape closes it, the page behind it does not
  // scroll, and widening the window past the phone layout closes it too.
  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKey = (e) => e.key === 'Escape' && setMenuOpen(false);
    const wide = window.matchMedia('(min-width: 901px)');
    const onWide = (e) => e.matches && setMenuOpen(false);
    const overflow = document.body.style.overflow;

    document.addEventListener('keydown', onKey);
    wide.addEventListener('change', onWide);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      wide.removeEventListener('change', onWide);
      document.body.style.overflow = overflow;
    };
  }, [menuOpen]);

  if (signedOut) {
    return (
      <div className="signed-out">
        <div className="signed-out__panel">
          <h1>Signed out</h1>
          <p>
            Cloudflare Access has ended the session. Your brands, posts and images are all still
            there — this screen just cannot reach them until you sign in again.
          </p>
          <button className="btn btn--primary" onClick={() => window.location.reload()}>
            Sign in again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="shell">
      <Sidebar user={user} waiting={waiting} open={menuOpen} onClose={() => setMenuOpen(false)} />

      {/* Phone only: the most-used screens along the bottom. */}
      <MobileTabBar waiting={waiting} menuOpen={menuOpen} onMenu={() => setMenuOpen(true)} />

      <main className="main">
        <Topbar menuOpen={menuOpen} onMenu={() => setMenuOpen(true)} />

        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/brands" element={<Brands />} />

          <Route path="/create" element={<Create />} />
          <Route path="/video" element={<Video />} />
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/drafts" element={<Drafts />} />
          <Route path="/inbox" element={<Inbox />} />

          {/* Comments, Messages, Auto-reply and Approvals are tabs on the
              Inbox now. The old addresses still resolve, so a bookmark or a
              link in an old email lands somewhere sensible rather than on the
              not-found screen. */}
          <Route path="/messages" element={<Navigate to="/inbox" replace />} />
          <Route path="/autoreply" element={<Navigate to="/inbox" replace />} />
          <Route path="/copilot" element={<Copilot />} />
          <Route path="/campaigns" element={<Campaigns />} />
          <Route path="/campaigns/:id" element={<Campaign />} />
          <Route path="/library" element={<Library />} />
          <Route path="/import" element={<Import />} />
          <Route path="/resize" element={<Resize />} />
          <Route path="/evergreen" element={<Evergreen />} />
          <Route path="/autoschedule" element={<AutoSchedule />} />
          <Route path="/hashtags" element={<Hashtags />} />
          <Route path="/bio" element={<BioPage />} />
          <Route path="/reviews" element={<Navigate to="/inbox" replace />} />
          <Route path="/team" element={<Team />} />
          <Route path="/competitors" element={<Competitors />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/predict" element={<Predictor />} />
          <Route path="/listening" element={<Listening />} />
          <Route path="/repurpose" element={<Repurpose />} />
          <Route path="/website" element={<Website />} />
          <Route path="/ads" element={<Ads />} />
          <Route path="/ads/new" element={<AdBuilder />} />
          <Route path="/costs" element={<Costs />} />
          <Route
            path="/help"
            element={
              <Placeholder title="Help & Support" phase="Later">
                The build plan and runbook live in the project’s docs folder for now.
              </Placeholder>
            }
          />
          <Route path="/export" element={<Export />} />
          <Route path="/settings" element={<Settings />} />

          <Route
            path="*"
            element={
              <Placeholder title="Not found" phase="—">
                That screen doesn’t exist.
              </Placeholder>
            }
          />
        </Routes>
      </main>
    </div>
  );
}
